
import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

// Use the values from our Supabase integration
const supabaseUrl = "https://ypmennmpmhttnsgeviul.supabase.co";
const supabaseKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlwbWVubm1wbWh0dG5zZ2V2aXVsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDEzNzY1MDQsImV4cCI6MjA1Njk1MjUwNH0.t92C9cQj0F8JQaw6_3x4zDvioXb2tz6C_i6b6ON8Ju8";

// Maximum number of retry attempts
const MAX_RETRIES = 3;

// Initial timeout (doubled with each retry)
const INITIAL_TIMEOUT = 60000; // Increased to 60 seconds initial timeout

// Helper function to implement retry logic with exponential backoff
const fetchWithRetry = async (url: string, options: RequestInit) => {
  let retries = 0;
  let lastError: Error | null = null;
  
  while (retries <= MAX_RETRIES) {
    const timeout = INITIAL_TIMEOUT * Math.pow(2, retries);
    console.log(`[SUPABASE] Attempt ${retries + 1}/${MAX_RETRIES + 1} for ${url} (timeout: ${timeout}ms)`);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn(`[SUPABASE TIMEOUT] Request to ${url} timed out after ${timeout}ms`);
        controller.abort();
      }, timeout);
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      return response;
    } catch (error: any) {
      lastError = error;
      
      // Don't retry if it was manually aborted
      if (error.name === 'AbortError' && error.message !== 'The operation was aborted.') {
        console.error(`[SUPABASE ERROR] ${url} ${error.message}`);
        retries++;
        
        if (retries <= MAX_RETRIES) {
          const backoffTime = 1000 * Math.pow(2, retries - 1);
          console.log(`[SUPABASE RETRY] Waiting ${backoffTime}ms before retry ${retries}/${MAX_RETRIES}`);
          await new Promise(resolve => setTimeout(resolve, backoffTime));
        }
      } else {
        // It was manually aborted or some other reason to not retry
        break;
      }
    }
  }
  
  // If we've exhausted all retries, throw the last error
  throw lastError || new Error('Request failed after maximum retries');
};

// Create and export the Supabase client with retries and extended timeouts
export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'x-application-name': 'travel-mindscape-blog'
    },
    // Replace the fetch implementation with our retry-capable version
    fetch: fetchWithRetry
  }
});

// Create an admin client for privileged operations (should only be used by admins)
// This will still respect RLS, but we can use it for admin-specific operations
export const getAdminClient = () => {
  return supabase;
};

// Helper function to handle Supabase errors consistently
export const handleSupabaseError = (error: any) => {
  console.error('Supabase error:', error);
  
  // Return a standardized error object
  return {
    message: error?.message || 'An unknown error occurred',
    code: error?.code || 'unknown_error',
    details: error?.details || null
  };
};

// Export the Supabase URL from our configuration for use in other parts of the app
export const SUPABASE_URL = supabaseUrl;

